import { ensureAnonAuth } from '../lib/anonAuth';
import { normalizeAudience } from '../core/bandiAudience';
import { normalizeSeatRole } from '../lib/seatRole';
import {
  HOLDER_DOC_ID, HOLDER_VIA, holderVerdict, personalSessionVerdict,
} from '../lib/deviceSession';

const LICENSE_STORAGE_KEY = 'semi-license';
const TRIAL_SESSION_KEY = 'semi-trial-session';
const STORAGE_PREFIX = 'semifab_edu_';

// ─────────────────────────────────────────────────────────────
// 서브도메인 공유 쿠키 (kredu.semifabai.com ↔ krsimul.semifabai.com)
// localStorage 는 origin 별 분리이므로, *.semifabai.com 사이에서 라이선스를 공유하려면 쿠키 필요.
// 무료 체험은 공유 대상 아님 (탭 단위 유지 정책).
// ─────────────────────────────────────────────────────────────
const SHARED_COOKIE_NAME = 'semifab_license';

function getSharedCookieDomain() {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (host.endsWith('.semifabai.com') || host === 'semifabai.com') return '.semifabai.com';
  return null; // localhost / 기타 도메인 — 공유 안 함 (host-only 쿠키)
}

function readSharedLicenseCookie() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${SHARED_COOKIE_NAME}=([^;]+)`));
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────
// 연간(365일) 코드 = 전권 초대 코드 — 등급이 문을 넘어가다 증발하던 자리
//
// 미니팹(process-center)은 이미 **개인 365일권이면 확장팩 전부**로 판정한다
// (저쪽 `licenseService.hasAllModules` · 저쪽 docs/DECISIONS.md #240 — 365 는 파는 등급이
// 아니라 전권 초대 코드라, 앞으로 만드는 확장팩이 자동으로 딸려가는 것이 의도다).
// 그 판정의 입력은 **plan 문자열**이고, 두 레포가 합의한 enum 은 `{일수}day` 다
// (docs/ADMIN-IMPLEMENTATION-GUIDE.md · 저쪽 docs/admin-integration.md §2).
//
// 그런데 개인(GAS) 코드의 plan 은 `3d`·`365d` 꼴로 온다
// (docs/SERVICE-CONFIG-CONTRACT.md 의 verify-license 응답 예시 `"plan": "3d"`).
// 저쪽 `planToDays` 는 그 꼴을 못 읽고 **`daysRemaining` 으로 폴백**하는데, 그 값은 매일
// 줄어든다 — 365일 코드도 이틀째면 364 라서 "365 이상"이 영영 거짓이 된다.
// 저쪽이 등급을 `daysRemaining` 이 아니라 `plan` 으로 재는 이유가 바로 그것인데,
// plan 을 못 읽는 순간 그 방어가 통째로 무력해진다.
//
// 실측(2026-09-17, 335일 남은 365 코드): kredu 포털의 엔지니어 트레이닝 카드는 팝업으로
// 막았고, 팝업을 지나 `?ui=process-center&landing=career` 로 들어가도 트레이닝 센터가
// 아니라 모드 선택이 떴다(모드 둘 — 가상 팹 투어 문도 없었다). **저쪽 코드는 멀쩡했다.**
//
// 그래서 공유 쿠키에 실을 때만 연간권의 plan 을 계약 enum(`365day`)으로 맞춘다.
// 3·7·14·30일은 **손대지 않는다** — 이 저장소의 코스 게이트가 `3d` 꼴로 표를 잡고 있어서
// (`MatrixPrototype.PLAN_ALLOWED_COURSES`) 쿠키가 localStorage 로 되돌아오는 경로에서
// 뜻이 바뀐다(`'3day'` 는 그 표에 없어 전 코스 통과가 된다). 그 표를 통째로 계약 enum 에
// 맞추는 일은 기관 좌석(`3day`)까지 새로 표에 걸리게 만들어 **별도 작업**이다.
// `365d`·`365day` 는 이 저장소의 어느 표에도 키가 없어 뜻이 안 바뀐다(게이트가 잠근다:
// `src/utils/__tests__/annualTier.test.js` 의 "되돌아와도 코스 게이트의 답이 같다").
// ─────────────────────────────────────────────────────────────

/** 계약 enum 의 연간권 표기 — 미니팹이 이 값으로 등급을 읽는다. */
export const ANNUAL_PLAN_ID = '365day';
/** 연간권 임계 — 저쪽 `ANNUAL_TIER_DAYS` 와 같은 수. */
export const ANNUAL_TIER_DAYS = 365;

/**
 * 구매 코스 일수 — 등급 판정의 **단일 소스**. 남은 일수가 아니라 산 일수다.
 *
 * 표기가 발급처마다 다르다(`365d` · `365day` · `365 days` · `Premium` …). 그래서 plan 에서
 * 일수를 읽고, 못 읽으면 `daysTotal`(구매 시점 고정) → `daysRemaining` 순으로 떨어진다.
 * ★`daysRemaining` 을 먼저 보면 안 된다 — 매일 줄어들어 365일권이 시간이 지나며 등급을 잃는다.
 * (미니팹 `licenseService.getPlanDays` 와 같은 규칙 — 두 레포가 같은 답을 내야 한다.)
 *
 * 숫자는 **통째로 일수 표기일 때만** 읽는다(`^숫자[d|day|days]?$`). 아무 데서나 숫자를 줍던
 * 종전 정규식은 `Premium2024` 같은 값에서 2024 를 뽑아 **안 산 코드에 전권을 준다.**
 */
export function planDaysOf(info) {
  const target = info ?? getLicenseInfo();
  if (!target) return 0;
  const key = String(target.plan ?? '').trim().toLowerCase();
  const m = key.match(/^(\d+)\s*(d|day|days)?$/);
  if (m) return Number(m[1]);
  const total = Number(target.daysTotal);
  if (Number.isFinite(total) && total > 0) return total;
  const left = Number(target.daysRemaining);
  return Number.isFinite(left) && left > 0 ? left : 0;
}

/**
 * 연간 **개인** 코드인가 — 미니팹에서 확장팩을 전부 갖는 그 등급(전권 초대 코드).
 *
 * 규칙도 예외 둘도 미니팹 쪽(`hasAllModules`)과 같은 모양이다. 여기서 더 넓히지 마라:
 *  · **기관 좌석** — 콘솔이 켜고 끈다(기관이 안 산 모듈이 열리면 계약 위반).
 *    기관 코드가 365일로 발급되는 순간 콘솔의 «끄는 수단»이 무의미해진다.
 *  · **무료 체험** — 공유 대상이 아니다.
 */
export function isAnnualPersonal(info) {
  const target = info ?? getLicenseInfo();
  if (!target) return false;
  if (target.isFreeTrial === true || target.plan === 'free-trial') return false;
  if (target.isInstitution === true) return false;
  return planDaysOf(target) >= ANNUAL_TIER_DAYS;
}

/** 공유 쿠키에 실을 모양 — 연간권만 plan 을 계약 enum 으로 맞춘다(위 주석). */
function sharedCookieLicense(license) {
  if (!isAnnualPersonal(license) || license.plan === ANNUAL_PLAN_ID) return license;
  return { ...license, plan: ANNUAL_PLAN_ID };
}

function writeSharedLicenseCookie(license) {
  if (typeof document === 'undefined' || !license) return;
  // 무료 체험은 서브도메인 공유 안 함
  if (license.isFreeTrial || license.plan === 'free-trial') return;
  if (!license.expiresAt) return;

  const expires = new Date(license.expiresAt);
  if (Number.isNaN(expires.getTime()) || expires <= new Date()) return;

  const value = encodeURIComponent(JSON.stringify(sharedCookieLicense(license)));
  const domain = getSharedCookieDomain();
  const domainPart = domain ? `; Domain=${domain}` : '';
  const securePart = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${SHARED_COOKIE_NAME}=${value}; Expires=${expires.toUTCString()}; Path=/; SameSite=Lax${domainPart}${securePart}`;
}

function clearSharedLicenseCookie() {
  if (typeof document === 'undefined') return;
  const domain = getSharedCookieDomain();
  const domainPart = domain ? `; Domain=${domain}` : '';
  document.cookie = `${SHARED_COOKIE_NAME}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/${domainPart}`;
}

/**
 * 페이지 로드 시: 공유 쿠키 ↔ localStorage 동기화.
 * - 쿠키가 있고 localStorage 가 비어 있거나 더 오래된 경우 → localStorage 복원
 * - 쿠키가 만료됐으면 정리
 * clearTrialOnFreshNavigation / consumeAdminURLPayload 보다 먼저 동작해야
 * 다른 서브도메인에서 입력한 코드가 자동 반영됨.
 */
(function syncFromSharedCookieOnLoad() {
  try {
    if (typeof window === 'undefined') return;
    const cookieLicense = readSharedLicenseCookie();
    if (!cookieLicense || !cookieLicense.expiresAt) return;

    const expires = new Date(cookieLicense.expiresAt);
    if (Number.isNaN(expires.getTime()) || expires <= new Date()) {
      clearSharedLicenseCookie();
      return;
    }

    const localRaw = localStorage.getItem(LICENSE_STORAGE_KEY);
    if (!localRaw) {
      // localStorage 비어 있음 → 쿠키에서 복원
      localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(cookieLicense));
      return;
    }

    let local;
    try { local = JSON.parse(localRaw); } catch { local = null; }
    if (!local) {
      localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(cookieLicense));
      return;
    }

    // localStorage 가 무료 체험인데 쿠키에 정식 라이선스가 있으면 업그레이드
    if (local.isFreeTrial || local.plan === 'free-trial') {
      localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(cookieLicense));
      sessionStorage.removeItem(TRIAL_SESSION_KEY);
      return;
    }

    // verifiedAt 비교 — 쿠키가 더 최신이면 동기화
    const localTs = new Date(local.verifiedAt || local.activatedAt || 0).getTime();
    const cookieTs = new Date(cookieLicense.verifiedAt || cookieLicense.activatedAt || 0).getTime();
    if (cookieTs > localTs) {
      localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(cookieLicense));
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[shared cookie sync] 처리 실패:', e);
  }
})();

/**
 * 이미 입장해 있던 연간권의 쿠키를 계약 enum 으로 맞춘다 — **코드를 다시 넣게 하지 않는다.**
 *
 * 쿠키는 코드를 검증할 때 한 번 쓰인다. 이 줄이 없으면 이미 들어와 있는 365 코드는
 * 로그아웃하고 코드를 다시 넣어야 미니팹이 등급을 본다 — 담당자에게는 "고쳤다는데 그대로"다.
 * 위 동기화 IIFE 다음에 온다(복원된 라이선스를 읽어야 하므로).
 *
 * ★`verifiedAt` 을 같이 올린다. **쿠키를 고쳐 쓰는 것만으로는 저쪽이 안 읽는다** — 양쪽의
 *  동기화가 `verifiedAt` 을 «쿠키가 더 최신인가»의 표식으로 쓰기 때문에, 이미 미니팹에
 *  한 번 들어갔던 브라우저는 저쪽 localStorage 에 같은 시각의 옛 라이선스를 들고 있어
 *  새 쿠키를 무시한다(2026-09-17 스크린샷의 그 브라우저가 정확히 그 상태다).
 *  localStorage 와 쿠키에 **같은 시각**을 적어, 저쪽만 갱신되고 이쪽은 제자리에 둔다.
 */
(function canonicalizeAnnualPlanForSharedCookie() {
  try {
    if (typeof window === 'undefined') return;
    const raw = localStorage.getItem(LICENSE_STORAGE_KEY);
    if (!raw) return;
    const license = JSON.parse(raw);
    if (!isAnnualPersonal(license)) return;
    const cookie = readSharedLicenseCookie();
    if (cookie && cookie.plan === ANNUAL_PLAN_ID) return; // 이미 맞다 — 다시 쓰지 않는다(한 번만 돈다)
    const healed = { ...license, verifiedAt: new Date().toISOString() };
    localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(healed));
    writeSharedLicenseCookie(healed);
  } catch { /* 쿠키 치유 실패가 입장을 막지 않는다 */ }
})();

/**
 * 모듈 로드 시 실행: 외부에서 진입(navigate)이면 체험 데이터를 자동 정리합니다.
 * - 새로고침(reload)이면 체험 유지 (체험 중 F5 눌러도 끊기지 않음)
 * - 외부 링크/주소창 진입(navigate)이면 체험 삭제 → 코드 입력 화면 표시
 */
(function clearTrialOnFreshNavigation() {
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    const isReload = nav && nav.type === 'reload';
    if (isReload) return; // 새로고침이면 체험 유지

    // 외부 진입(navigate) → 체험 데이터 삭제
    const raw = localStorage.getItem(LICENSE_STORAGE_KEY);
    if (!raw) return;
    const license = JSON.parse(raw);
    if (license.isFreeTrial || license.plan === 'free-trial') {
      localStorage.removeItem(LICENSE_STORAGE_KEY);
      sessionStorage.removeItem(TRIAL_SESSION_KEY);
    }
  } catch { /* 무시 */ }
})();

/**
 * URL 쿼리 파라미터로 들어온 admin 발급 라이센스를 처리한다.
 * - ?code=&expiresAt=&plan=&typeName=&cohortId=&studentSeq=&forceReset=
 * - localStorage에 저장 후 URL 정리(쿼리 제거)
 * - forceReset=1 이면 해당 코드 prefix 키를 모두 삭제
 *
 * 모듈 로드 시 1회 실행. App 부트보다 먼저 동작.
 */
(function consumeAdminURLPayload() {
  try {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const expiresAt = params.get('expiresAt');
    if (!code || !expiresAt) return;
    // 기관 좌석 코드(CLS-)는 URL 페이로드를 절대 신뢰하지 않는다.
    // → Firestore seats/{code} 서버 검증(verifyInstitutionCode)으로만 입장.
    if (code.toUpperCase().startsWith('CLS-')) return;

    const plan = params.get('plan') || 'unknown';
    const typeName = params.get('typeName') || plan;
    const daysRemaining = parseInt(params.get('daysRemaining') || '0', 10);
    const cohortId = params.get('cohortId') || null;
    const studentSeq = params.get('studentSeq') ? parseInt(params.get('studentSeq'), 10) : null;
    const forceReset = params.get('forceReset') === '1';

    // forceReset 처리: 진행도 prefix 키 삭제
    if (forceReset) {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(`${STORAGE_PREFIX}${code}_`)) {
          keysToRemove.push(k);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    }

    const license = {
      code,
      plan,
      typeName,
      daysTotal: daysRemaining || 0,
      daysRemaining,
      activatedAt: new Date().toISOString(),
      expiresAt,
      verifiedAt: new Date().toISOString(),
      cohortId,
      studentSeq,
      isFreeTrial: false,
    };
    localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(license));
    writeSharedLicenseCookie(license);

    // URL에서 민감 파라미터 제거 (forceReset은 1회용이므로 반드시 제거)
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);
  } catch (e) {
    console.warn('[admin URL payload] 처리 실패:', e);
  }
})();

// 이 주소는 **빌드할 때** Secret 에서 구워진다 — Secret 만 바꾸면 운영 화면은 옛 주소를 계속 부른다. 바꾼 뒤 한 번 배포해야 한다.
//  2026-10-30: Apps Script 를 «배포 관리 → 새 버전» 으로 세 번 올려도 옛 배포(…258o)가 옛 함수로 답해 새 배포(…IXfo)로
//  옮겼다. Secret 을 바꾼 뒤에도 kredu 는 …258o 를 불렀다(다시 빌드 전). 옛 배포는 이 배포가 나간 뒤에만 보관한다.
const GAS_URL = process.env.REACT_APP_APPS_SCRIPT_PURCHASE_URL;

/**
 * GAS에 POST 요청을 보내는 헬퍼.
 * CORS 전략: Content-Type: text/plain → simple request (preflight 없음)
 */
async function gasPost(payload) {
  if (!GAS_URL) {
    throw new Error('API URL이 설정되지 않았습니다.');
  }

  const res = await fetch(GAS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    redirect: 'follow',
    body: JSON.stringify({ siteId: 'semiconductor', ...payload }),
  });

  if (!res.ok) {
    throw new Error('서버 오류가 발생했습니다.');
  }

  return res.json();
}

/**
 * GAS 응답의 모듈 엔타이틀먼트를 `serviceConfig` 한 모양으로 정규화한다.
 *
 * 왜(2026-09 실측 누락): 기관 경로(`verifyInstitutionCode`)는 좌석의 `serviceConfig` 를 쿠키에
 * 실어 process-center 가 `isModuleEnabled(id)` 로 읽는데, **개인 경로(`verifyLicense`)만 그 필드를
 * 통째로 떨구고 있었다** — 그래서 개인이 취업 챌린지를 사도 미니팹에 문이 안 생겼다.
 *
 * 발급 측이 어느 모양으로 보내든 받는다(계약을 한쪽에 강요하지 않는다):
 *   modules: 'recipe.decisionLabEtchSet,metrology.sem'   (콤마 문자열)
 *   modules: ['recipe.decisionLabEtchSet']               (배열)
 *   serviceConfig: { schemaVersion, enabled: {...} }      (기관과 같은 모양 — 그대로 통과)
 * 아무것도 없으면 **null** 을 돌려 필드 자체를 안 싣는다(종전 동작 그대로 — 모르는 id 는 off 규약).
 */
export function serviceConfigFromGas(data) {
  if (!data) return null;
  if (data.serviceConfig && typeof data.serviceConfig === 'object' && data.serviceConfig.enabled) {
    return data.serviceConfig;
  }
  const raw = data.modules;
  const list = Array.isArray(raw)
    ? raw
    : (typeof raw === 'string' ? raw.split(',') : []);
  const enabled = {};
  list.forEach((entry) => {
    const id = String(entry || '').trim();
    // 점 네임스페이스 id만 — 규약 밖 토큰은 조용히 버린다(process-center serviceConfigFromModulesParam 과 같은 규칙).
    if (/^[A-Za-z]+(\.[A-Za-z0-9]+)+$/.test(id)) enabled[id] = true;
  });
  return Object.keys(enabled).length ? { schemaVersion: 1, enabled } : null;
}

/**
 * 발급·활성화 건수(운영사 콘솔 «개인 미션» 탭·등대) — GAS `issued-stats`(랜딩 레포 gas/VERIFY-API-SPEC.md).
 * 코드·이메일 없이 집계만 온다. 학생 코드 검증과 같은 통로(gasPost)를 쓴다 — URL 을 두 벌 두지 않는다.
 * @returns {Promise<{total:object, byPlan:object, days:number, unknownPlan:number}>} 실패는 throw(호출자가 null 로 가른다)
 */
export async function fetchIssuedStats(days = 7) {
  const data = await gasPost({ action: 'issued-stats', days });
  if (!data || data.success !== true) throw new Error((data && data.message) || 'issued-stats 실패');
  return data;
}

/**
 * 이용권 코드를 GAS 서버에서 검증합니다.
 * 성공 시 localStorage에 라이선스 정보를 저장합니다.
 *
 * claimSession — 사람이 코드칸에 넣은 입장일 때만 true(한 코드 = 한 기기, lib/deviceSession.js).
 *   GAS 는 이때만 sessionToken 을 새로 발급해 이전 기기를 밀어낸다. 기본값이 false 인 이유:
 *   verify-license 는 이 화면 말고도 묻는다(창고 personalView 의 실제/테스트 판정, 이용권 서버 감시,
 *   미니팹). 그쪽 질의가 토큰을 갈면 학생은 자기 기기에서 아무 일 없이 쫓겨난다.
 *   옛 GAS 는 이 필드를 모르고 토큰도 안 준다 — 그러면 종전 그대로(기기 제한 없음)다.
 */
export async function verifyLicense(code, { claimSession = false } = {}) {
  const data = await gasPost({
    action: 'verify-license',
    code: code.trim().toUpperCase(),
    ...(claimSession ? { claimSession: true } : {}),
  });

  if (data.success) {
    const license = {
      code: data.code,
      plan: data.plan,
      typeName: data.typeName,
      daysTotal: data.daysTotal,
      daysRemaining: data.daysRemaining,
      activatedAt: data.activatedAt,
      expiresAt: data.expiresAt,
      verifiedAt: new Date().toISOString(),
      isFreeTrial: false, // 일반 코드 → 체험 플래그 명시적 해제
    };
    // 모듈 엔타이틀먼트(취업 챌린지 등) — 있을 때만 싣는다. 없으면 필드 자체가 없다(종전 동작).
    const serviceConfig = serviceConfigFromGas(data);
    if (serviceConfig) license.serviceConfig = serviceConfig;
    // 이 기기의 세션 토큰 — validatePersonalSession 이 GAS 에 «아직 내 것인가»를 물을 때 낸다.
    if (typeof data.sessionToken === 'string' && data.sessionToken) license.sessionToken = data.sessionToken;
    localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(license));
    writeSharedLicenseCookie(license);
    return data;
  } else {
    throw new Error(data.message || '알 수 없는 오류가 발생했습니다.');
  }
}

/**
 * 기관 좌석 코드(CLS-)를 Firestore `seats/{code}` 에서 직접 검증합니다.
 * 개인(GAS) 경로와 완전히 분리 — GAS 호출 없음, 입장마다 서버 확인(회수·만료 즉시 반영).
 * Firebase 모듈은 동적 import 로 불러와 초기 번들에 영향 주지 않습니다.
 */
/**
 * QR 입장 — 반 코드 하나로 빈 자리를 하나 받아 그대로 입장한다.
 *
 * 좌석을 고르는 일은 **서버(joinClass 함수)만** 한다. "빈 좌석 아무거나"를 고르려면 그 반의
 * 좌석 목록을 읽어야 하는데, 학생에게 그 권한을 주면 반 전체 코드가 노출된다
 * (코드 하나가 곧 그 자리의 자격이다). functions/joinClass.js 주석 참고.
 *
 * 한 번 자리를 받은 브라우저는 그 자리를 기억한다 — 새로고침하고 또 찍어서 두 자리를
 * 먹는 실수를 막는다(완벽한 방어가 아니라 실수 방지다).
 */
const JOINED_SEAT_KEY = 'semifab_joined_seat';

function fnEndpoint(name) {
  // 프로젝트 id 는 이미 클라가 들고 있다 — 따로 설정하지 않아도 동작하게 한다.
  // (설정을 하나 더 요구하면 "배포했는데 QR 만 안 된다"가 된다.)
  const pid = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'ai-asset-manager-9e96c';
  return `https://asia-northeast3-${pid}.cloudfunctions.net/${name}`;
}

function joinEndpoint() {
  return process.env.REACT_APP_JOIN_URL || fnEndpoint('joinClass');
}

/**
 * 입장 창을 연 채로 붙든다 — 강의자가 QR 을 띄우고 있는 동안 주기적으로 부른다.
 * 닫기는 close:true. 못 보내도(탭이 죽어도) 서버가 시한부로 열었으므로 저절로 닫힌다.
 *
 * 실패해도 **던지지 않는다.** 이건 화면의 곁다리 동작이라, 여기서 터지면 QR 자체가 안 뜬다.
 * 대신 결과를 돌려줘 화면이 "지금 열려 있나"를 사실대로 말할 수 있게 한다(규칙 3).
 *
 * @returns {Promise<{ok:boolean, joinOpenUntil:number|null, adminOpen:boolean}|null>} null = 못 함
 */
export async function holdJoinOpen(instructorCode, { close = false } = {}) {
  if (!instructorCode) return null;
  try {
    const res = await fetch(fnEndpoint('holdJoinOpen'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ instructorCode, close }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * 이미 있는 자리를 이 기기에서 이어서 쓴다 — QR 되묻기에서 "그 자리로"를 골랐을 때.
 * 코드로 들어오는 것과 같은 길이라, 검증도 같은 함수가 한다.
 */
export async function takeJoinedSeat(joinCode, seatCode) {
  const lic = await verifyInstitutionCode(seatCode, { via: 'qr' });
  try {
    localStorage.setItem(JOINED_SEAT_KEY, JSON.stringify({ joinCode, code: seatCode }));
  } catch { /* 기억 못 해도 입장은 된다 — 다음에 또 물어볼 뿐이다 */ }
  return lic;
}

export async function joinClassByCode(rawJoinCode, rawName, { forceNew = false } = {}) {
  const joinCode = (rawJoinCode || '').trim().toUpperCase();
  const name = (rawName || '').replace(/\s+/g, ' ').trim();
  if (!joinCode) throw new Error('입장 코드가 없습니다.');
  if (!name) throw new Error('이름을 입력해 주세요.');

  // 이미 이 브라우저가 이 반에서 자리를 받았으면 그 자리를 그대로 쓴다.
  try {
    const prev = JSON.parse(localStorage.getItem(JOINED_SEAT_KEY) || 'null');
    if (prev && prev.joinCode === joinCode && prev.code) {
      return await verifyInstitutionCode(prev.code, { via: 'qr' });
    }
  } catch { /* 저장값이 깨졌으면 새로 받는다 */ }

  let res;
  try {
    res = await fetch(joinEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ joinCode, name, forceNew }),
    });
  } catch {
    throw new Error('연결에 실패했어요. 네트워크를 확인하고 다시 시도해 주세요.');
  }

  const data = await res.json().catch(() => ({}));

  // 같은 이름으로 이미 받아 둔 자리가 있다 — 실패가 아니라 **되묻는 자리**다.
  // 이름만 보고 합치면 동명이인 둘이 한 자리를 나눠 쓴다(진도·만족도가 뒤섞인다).
  // 화면이 물어보고, 답을 받아 다시 부른다(이어서 = takeSeatCode / 새로 = forceNew).
  if (res.status === 409 && data.error === 'name_taken' && data.code) {
    const err = new Error(data.message || '같은 이름의 자리가 이미 있어요.');
    err.code = 'name_taken';
    err.seatCode = data.code;
    err.seq = data.seq || null;
    throw err;
  }

  if (!res.ok || !data.code) {
    // 서버가 준 사람 말을 그대로 보여준다 — "실패했습니다"만 뜨면 담당자도 못 고친다.
    throw new Error(data.message || '자리를 배정하지 못했어요.');
  }

  try {
    localStorage.setItem(JOINED_SEAT_KEY, JSON.stringify({ joinCode, code: data.code, name }));
  } catch { /* 저장 실패는 무시 — 다음에 또 받을 뿐 */ }

  return await verifyInstitutionCode(data.code, { via: 'qr' });
}

function holderRef(fs, db, code) {
  return fs.doc(db, 'seats', code, 'holder', HOLDER_DOC_ID);
}

/**
 * 좌석 주인을 이 기기로 적는다. 실패해도 **입장은 막지 않는다** — 막으면 쓰기 한 번 튄 것으로
 * 학생이 수업에 못 들어온다. 그 대신 이전 기기가 안 닫힐 뿐이고, 이 기기는 다음에 코드를 넣을 때
 * 다시 적는다. 이유는 콘솔에 남긴다(조용히 삼키지 않는다).
 * @returns {Promise<boolean>} 적었나
 */
async function claimSeatHolder(fs, db, code, uid, via) {
  if (!uid) return false;
  try {
    await fs.setDoc(holderRef(fs, db, code), {
      uid,
      claimedAt: fs.serverTimestamp(),
      via: HOLDER_VIA.includes(via) ? via : 'code',
    });
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[seat holder] 좌석 주인을 적지 못했습니다 — 이전 기기가 닫히지 않습니다:', e?.code || e?.message || e);
    return false;
  }
}

/**
 * via — 사람이 코드를 넣은 길(lib/deviceSession.HOLDER_VIA). 이 함수를 부르는 곳은 전부 사람이 코드를
 *   넣은 입장이라(코드칸·좌석 링크·QR) 부를 때마다 이 기기가 좌석 주인이 된다(한 코드 = 한 기기).
 *   새로고침·재방문의 자동 입장은 이 함수를 부르지 않는다 — revalidateInstitutionSession 이 확인만 한다.
 *   ★자동 입장 경로에서 이 함수를 부르지 마라: 사람 손 없는 새로고침이 좌석을 가져가 지금 쓰는 기기를 닫는다.
 */
export async function verifyInstitutionCode(rawCode, { via = 'code' } = {}) {
  const code = (rawCode || '').trim().toUpperCase();
  if (!code.startsWith('CLS-')) throw new Error('기관 좌석 코드가 아닙니다.');

  // webpackPrefetch: 브라우저가 한가할 때 미리 받아만 둔다(실행은 안 한다 — 지금 이
  // 자리에 와야 실행된다). firebase/firestore 는 원본 1.7MB 짜리 별도 덩어리라,
  // 안 그러면 학생이 코드를 넣고 '확인'을 누른 **그때** 처음 받기 시작한다.
  const [{ db, auth }, fs, fbAuth] = await Promise.all([
    import(/* webpackPrefetch: true */ '../admin/firebase/config'),
    import(/* webpackPrefetch: true */ 'firebase/firestore'),
    import(/* webpackPrefetch: true */ 'firebase/auth'),
  ]);
  if (!db || !auth) throw new Error('서버 연결이 구성되지 않았습니다.');

  // 익명 Auth — 규칙상 isSignedIn() 통과 + rate-limit 토대.
  // 삼키지 않는다: 여기서 실패하면 아래 읽기가 "권한 없음"으로 떨어져
  // 학생에게는 **코드가 틀린 것처럼** 보인다(실제로 그 사고가 났다).
  const user = await ensureAnonAuth(auth, fbAuth);

  const ref = fs.doc(db, 'seats', code);
  const snap = await fs.getDoc(ref);
  if (!snap.exists()) throw new Error('잘못된 코드입니다.');
  const s = snap.data();
  if (s.status !== 'active') throw new Error('종료되었거나 회수된 좌석입니다.');

  const expiresMs = s.expiresAt?.toMillis ? s.expiresAt.toMillis() : new Date(s.expiresAt).getTime();
  if (Number.isFinite(expiresMs) && Date.now() > expiresMs) {
    throw new Error('이용 기간이 만료되었습니다.');
  }

  // 강제 리셋 대기 → 이 코드의 로컬 진행도 키 정리
  if (s.forceResetPending) {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(`${STORAGE_PREFIX}${code}_`)) keysToRemove.push(k);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  }

  // 이 기기가 좌석 주인 — 이전 기기는 주인 문서를 지켜보다 닫힌다(watchSeatHolder).
  // 좌석이 실재하고 유효한 것을 위에서 확인한 **뒤에** 쓴다(규칙 2 — 없는 좌석 밑에 문서를 만들지 않는다).
  await claimSeatHolder(fs, db, code, user?.uid || auth.currentUser?.uid, via);

  const expiresAtISO = new Date(expiresMs).toISOString();
  const daysRemaining = Math.max(0, Math.ceil((expiresMs - Date.now()) / (24 * 60 * 60 * 1000)));
  const license = {
    code,
    plan: `${s.duration}day`,
    typeName: `${s.duration}일 과정`,
    daysTotal: s.duration || daysRemaining,
    daysRemaining,
    activatedAt: new Date().toISOString(),
    expiresAt: expiresAtISO,
    verifiedAt: new Date().toISOString(),
    cohortId: s.classId || null,
    studentSeq: s.seq || null,
    isFreeTrial: false,
    isInstitution: true,
    // 서비스 구성(어떤 모듈을 켤지) — 운영사 콘솔이 기관 단위로 세팅한 값이 좌석에 복제돼 있다.
    // 공유 쿠키(semifab_license)에 함께 실어 process-center 가 isModuleEnabled(id) 로 읽는다.
    serviceConfig: s.serviceConfig || null,
    // PDF 워터마크용 기관 식별코드(입장 코드와 무관한 추적 전용 난수)
    wmId: s.wmId || null,
    // 학습자 배경 — 클래스 생성 시 콘솔이 좌석에 실어둔 값. 반디가 답의 깊이·어휘를 맞춘다.
    // canonical(highschool/university/professional) 이 아니면 미상 처리(null).
    audience: normalizeAudience(s.audience),
    // 좌석의 역할 — 학생앱이 이 값으로 강의자 화면(서고의 '담기')을 가른다.
    // 역할이 없는 옛 좌석은 학생. 학생은 자기 좌석의 role 을 못 고친다(rules 화이트리스트).
    role: normalizeSeatRole(s.role),
    // 이 반이 "강의자가 연 것만" 보는 반인가. 값이 없는 옛 좌석은 게이트 없음 = 전부 열림.
    lessonGated: s.lessonGated === true,
    // 강의자가 계획한 총 주차. 없으면 null — 화면은 "N주 중"을 말하지 않는다(지어내지 않는다).
    plannedWeeks: s.plannedWeeks > 0 ? s.plannedWeeks : null,
    // 입장 QR — 강의자가 자기 화면에서 띄운다. 클래스 문서는 담당자만 읽으므로 좌석 사본이다.
    // 학생 좌석에는 싣지 않는다 — 쓸 데가 없고, 있으면 학생이 QR 을 퍼뜨릴 수 있다.
    joinCode: s.role === 'instructor' ? (s.joinCode || null) : null,
    joinOpen: s.role === 'instructor' ? s.joinOpen === true : false,
  };
  localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(license));
  writeSharedLicenseCookie(license);

  // 활동 표시 + 리셋 플래그 소거 (실패해도 입장은 허용)
  try {
    await fs.updateDoc(ref, {
      lastActiveAt: fs.serverTimestamp(),
      forceResetPending: false,
    });
  } catch { /* 권한/네트워크 → 무시 */ }

  return license;
}

/**
 * 기관 좌석 세션을 Firestore 와 재대조합니다 (즉시 회수 보장용).
 * 캐시된 라이선스로 입장한 뒤에도, 앱 로드 때 좌석이 아직 유효한지 다시 확인한다.
 * 좌석 주인도 본다 — **확인만 하고 가져오지 않는다**(자동 입장이 가져오면 사람 손 없는 새로고침이 지금 쓰는 기기를 닫는다).
 * @returns {'valid'|'invalid'|'taken'|'error'}
 *   - 'invalid': 좌석 없음/회수/만료 → 호출측에서 로그아웃 처리
 *   - 'taken'  : 다른 기기가 이 코드를 넣었다 → 호출측에서 닫고 그 사실을 알린다
 *   - 'error' : 네트워크/구성 오류 → fail-open(세션 유지). 일시 오류로 학생을 끊지 않음.
 */
export async function revalidateInstitutionSession(rawCode) {
  const code = (rawCode || '').trim().toUpperCase();
  if (!code.startsWith('CLS-')) return 'error';
  try {
    const [{ db, auth }, fs, fbAuth] = await Promise.all([
      import('../admin/firebase/config'),
      import('firebase/firestore'),
      import('firebase/auth'),
    ]);
    if (!db || !auth) return 'error';
    // 로그인을 못 했으면 'invalid'(코드가 틀림)가 아니라 'error'(못 봤음)다 — 규칙 3.
    let user;
    try { user = await ensureAnonAuth(auth, fbAuth); } catch { return 'error'; }

    const snap = await fs.getDoc(fs.doc(db, 'seats', code));
    if (!snap.exists()) return 'invalid';
    const s = snap.data();
    if (s.status !== 'active') return 'invalid';
    const expiresMs = s.expiresAt?.toMillis ? s.expiresAt.toMillis() : new Date(s.expiresAt).getTime();
    if (Number.isFinite(expiresMs) && Date.now() > expiresMs) return 'invalid';
    // 주인을 못 읽었으면 좌석은 유효한 것으로 둔다 — 못 본 것을 '남의 것'으로 치지 않는다.
    let holder = null;
    try {
      const h = await fs.getDoc(holderRef(fs, db, code));
      holder = h.exists() ? h.data() : null;
    } catch { holder = null; }
    if (holderVerdict(holder, user?.uid || auth.currentUser?.uid) === 'taken') return 'taken';
    refreshCachedServiceConfig(code, s.serviceConfig);
    return 'valid';
  } catch {
    return 'error';
  }
}

/**
 * 입장해 있는 동안 좌석 주인을 지켜본다 — 다른 기기가 이 코드를 넣는 그 순간 onTaken 을 부른다.
 * 주인 문서는 코드를 넣을 때만 바뀌므로(하트비트는 좌석 문서에 쓴다) 지켜보는 값이 거의 들지 않는다.
 * 못 붙거나 끊겨도 닫지 않는다(fail-open — 일시 오류로 학생을 끊지 않는다).
 * @returns {() => void} 그만 지켜보기
 */
export function watchSeatHolder(rawCode, onTaken) {
  const code = (rawCode || '').trim().toUpperCase();
  if (!code.startsWith('CLS-') || typeof onTaken !== 'function') return () => {};
  let stopped = false;
  let unsub = null;
  (async () => {
    try {
      const [{ db, auth }, fs, fbAuth] = await Promise.all([
        import('../admin/firebase/config'),
        import('firebase/firestore'),
        import('firebase/auth'),
      ]);
      if (!db || !auth || stopped) return;
      const user = await ensureAnonAuth(auth, fbAuth);
      const myUid = user?.uid || auth.currentUser?.uid;
      if (stopped) return;
      unsub = fs.onSnapshot(
        holderRef(fs, db, code),
        (snap) => {
          if (stopped) return;
          if (holderVerdict(snap.exists() ? snap.data() : null, myUid) === 'taken') onTaken();
        },
        () => { /* 못 봄 — 닫지 않는다 */ },
      );
      if (stopped) unsub();
    } catch { /* 못 붙음 — 닫지 않는다. 다음 로드 때 revalidate 가 다시 본다 */ }
  })();
  return () => {
    stopped = true;
    if (unsub) unsub();
  };
}

/**
 * 개인 코드가 아직 이 기기의 것인가 — GAS validate-session.
 * 토큰이 없는 기기(v2 전에 넣었거나 옛 GAS 가 토큰을 안 줬다)도 묻는다: 그 뒤에 다른 기기가 코드를
 * 넣었으면 GAS 에 토큰이 생겨 있으니 이 기기는 남의 것이 된다. 토큰이 아무에게도 없으면 GAS 가 valid 라 한다.
 * @returns {Promise<'valid'|'taken'|'unknown'>} unknown 이면 아무것도 하지 않는다
 */
export async function validatePersonalSession() {
  const info = getLicenseInfo();
  if (!info || !info.code || info.isInstitution) return 'unknown';
  if (info.isFreeTrial || info.plan === 'free-trial') return 'unknown';
  try {
    const data = await gasPost({
      action: 'validate-session',
      code: String(info.code).trim().toUpperCase(),
      sessionToken: info.sessionToken || null,
    });
    return personalSessionVerdict(data);
  } catch {
    return 'unknown';
  }
}

/**
 * 좌석의 서비스 구성이 캐시와 다르면 캐시(localStorage)와 공유 쿠키를 새 값으로 바꾼다.
 *
 * 왜(2026-09-29): 구성은 코드를 **처음 입력할 때** 좌석에서 한 번 받아 두고 계속 썼다. 운영사가
 * 진행 중인 반에 확장팩을 반영해도(콘솔 «기존 반에도 반영») 학생은 코드를 다시 쳐야 바뀌었다.
 * 로드 때마다 어차피 좌석 문서를 읽으므로(위 재대조) 그 값으로 맞춘다 — 요청이 늘지 않는다.
 * 구성만 바꾼다: 다른 필드(만료·역할·QR…)는 종전대로 입장 때 정해진 값을 쓴다.
 * 이 캐시가 다른 좌석의 것이면(코드가 다르면) 손대지 않는다.
 */
function refreshCachedServiceConfig(code, seatConfig) {
  try {
    const cur = getLicenseInfo();
    if (!cur || cur.code !== code) return;
    const next = seatConfig || null;
    if (JSON.stringify(cur.serviceConfig || null) === JSON.stringify(next)) return;
    // verifiedAt 도 올린다 — 서브도메인 사이 쿠키↔localStorage 동기화가 «더 최신»을 이것으로 가른다
    // (syncFromSharedCookieOnLoad). 안 올리면 다른 쪽의 옛 사본이 같은 시각이라 새 구성을 안 받는다.
    const license = { ...cur, serviceConfig: next, verifiedAt: new Date().toISOString() };
    localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(license));
    writeSharedLicenseCookie(license);
  } catch { /* 저장소가 막힌 브라우저 — 다음 입장 때 받는다 */ }
}

/**
 * localStorage에 저장된 라이선스가 유효한지(만료 전인지) 확인합니다.
 */
export function isLicenseValid() {
  const raw = localStorage.getItem(LICENSE_STORAGE_KEY);
  if (!raw) return false;

  try {
    const license = JSON.parse(raw);

    // 무료 체험은 현재 탭(세션)에서만 유효
    if (license.isFreeTrial || license.plan === 'free-trial') {
      if (!sessionStorage.getItem(TRIAL_SESSION_KEY)) {
        // 다른 탭이거나 탭을 닫았다 다시 열은 경우 → 체험 데이터 삭제
        localStorage.removeItem(LICENSE_STORAGE_KEY);
        return false;
      }
    }

    const now = new Date();
    const expires = new Date(license.expiresAt);
    return now < expires;
  } catch {
    return false;
  }
}

/**
 * 저장된 라이선스 정보를 반환합니다.
 */
export function getLicenseInfo() {
  const raw = localStorage.getItem(LICENSE_STORAGE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// 연간 등급 판정(`planDaysOf` · `isAnnualPersonal` · `ANNUAL_TIER_DAYS`)은 **이 파일 위쪽**,
// 공유 쿠키 헬퍼 옆에 있다. 여기로 내리지 마라 — 로드 시점 IIFE
// (`canonicalizeAnnualPlanForSharedCookie`)가 그 판정을 부르는데, `ANNUAL_TIER_DAYS` 가
// const 라 아래에 있으면 TDZ 로 죽는다(화면이 통째로 안 뜬다).

/**
 * 로그아웃: 라이선스 정보만 삭제합니다.
 * 진행도(semifab_edu_*)는 유지하여 재구매 시 이어서 학습할 수 있습니다.
 */
export function logout() {
  localStorage.removeItem(LICENSE_STORAGE_KEY);
  sessionStorage.removeItem(TRIAL_SESSION_KEY);
  clearSharedLicenseCookie();
}

/**
 * 라이선스 정보를 localStorage에서 제거합니다.
 */
export function clearLicense() {
  localStorage.removeItem(LICENSE_STORAGE_KEY);
  sessionStorage.removeItem(TRIAL_SESSION_KEY);
  clearSharedLicenseCookie();
}

/**
 * 무료 체험 모드를 활성화합니다.
 * 활성화 후 24시간 동안만 유효하며, 만료 후 코드 입력 화면으로 돌아갑니다.
 */
export function activateFreeTrial() {
  const now = new Date();
  const expires = new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24시간 후 만료
  const trialData = {
    code: 'FREE-TRIAL',
    plan: 'free-trial',
    typeName: '무료 체험',
    daysTotal: 1,
    daysRemaining: 1,
    activatedAt: now.toISOString(),
    expiresAt: expires.toISOString(),
    verifiedAt: now.toISOString(),
    isFreeTrial: true,
  };
  localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(trialData));
  // 현재 탭에서만 체험 유효하도록 세션 마커 설정
  sessionStorage.setItem(TRIAL_SESSION_KEY, 'true');
  return trialData;
}

/**
 * 현재 라이선스가 무료 체험인지 확인합니다.
 */
/**
 * 개인 미션 코드인가 — 엔지니어 트레이닝 **말고는 아무 데도 못 간다**(2026-10-08 담당자: "완전히 막는 것도").
 *
 * 개인 미션 상품(GAS 3d·7d)은 N열에 `preview.trainingOnly` 를 싣는다. 기관 좌석의 같은 스위치(콘솔
 * «테스트 모드(영업·시연용)»)는 **화면에 장막만** 씌우고 권한은 그대로 두기로 한 것이라(콘솔 카탈로그
 * 설명 그대로) 기관은 여기서 빼고, 개인 코드만 실제로 막는다. 그 코드의 plan('3d')이 3일 과정을 열어
 * 주던 것을 주소·되살린 세션·옛 화면(?legacy=1)으로 우회해 들어가지 못하게 하는 자리다.
 *
 * 문자열 'preview.trainingOnly' 는 serviceCatalog.PREVIEW_TRAINING_ONLY 와 같아야 한다 — 이 파일은 첫 화면
 * 덩어리라 카탈로그(콘솔 데이터)를 import 하지 않는다. 같은지는 careerChallenge.test.js 가 잠근다.
 */
/**
 * 주제 교재(서고)만 잠근 개인 코드인가 — 직군 패스(본진 GAS 10일·60일, 2026-10-20 담당자 «패스권으로 미니팹과 코스도 열고 · 서고는 닫고
 *  비활성으로 잠그고 · 반디는 잠그자»). 트레이닝만(isTrainingOnlyCode)과 달리 코스·미니팹·트레이닝은 열고 «주제 교재» 카드만 보이되 잠근다.
 *  기관 좌석은 제외(콘솔이 정한다). 문자열은 본진 GAS SEMI_PASS_SHELL 과 같아야 한다(careerChallenge.test 가 대조).
 */
export const LIBRARY_LOCKED_FLAG = 'preview.libraryLocked';
export function isLibraryLockedCode(info = getLicenseInfo()) {
  if (!info || info.isInstitution) return false;
  return info.serviceConfig?.enabled?.[LIBRARY_LOCKED_FLAG] === true;
}
export const TRAINING_ONLY_FLAG = 'preview.trainingOnly';
export function isTrainingOnlyCode(info = getLicenseInfo()) {
  if (!info || info.isInstitution) return false;
  return info.serviceConfig?.enabled?.[TRAINING_ONLY_FLAG] === true;
}

export function isFreeTrial() {
  const info = getLicenseInfo();
  return info?.isFreeTrial === true || info?.plan === 'free-trial';
}
